import platform from '@tencent/wphk-platform-adapter';
import compareVersions from 'compare-versions';

const DEFAULT_VERSION = '0.0.0';

export const getUserAgent = (): string => platform.navigator.userAgent || '';

/**
 * 获取OS信息
 */
export const getOSInfoObj = (): { type: 'iphone' | 'android' | 'none', version: string } => {
  let pattern = /(iPhone OS |iPad; CPU OS )([\d_]+)/i;
  let wc = getUserAgent().match(pattern);
  if (wc && wc.length >= 3) return { type: 'iphone', version: wc[2]?.replace(/_/g, '.') || DEFAULT_VERSION };
  pattern = /Android ([\d.]+)/i;
  wc = getUserAgent().match(pattern);
  if (wc && wc.length >= 2) return { type: 'android', version: wc[1] || DEFAULT_VERSION };
  return { type: 'none', version: DEFAULT_VERSION };
};

/** 获取系统信息文本 */
export const getOSInfoText = () => {
  const os = getOSInfoObj();
  return `${os.type} ${os.version}`;
};

/**
 * 获取微信版本
 */
export const getWeixinVer = (): string => {
  const pattern1 = /MicroMessenger\/[\d.]+\((.*)\)/i;
  const pattern2 = /MicroMessenger\/([\d.]+)/i;

  const wc = getUserAgent().match(pattern1) || getUserAgent().match(pattern2);
  if (!wc) return DEFAULT_VERSION;

  const version = wc[1]?.slice(3) || DEFAULT_VERSION;
  if (!version) return DEFAULT_VERSION;

  const result: number[] = [];
  result.push(parseInt(version.slice(0, 1), 16) || 0);
  result.push(parseInt(version.slice(1, 3), 16) || 0);
  result.push(parseInt(version.slice(3, 5), 16) || 0);
  return result.join('.');
};

/**
 * 比较版本1和版本2的高低, 0 相等，-1 v1 < v2， 1 v1 > v2
 * @param v1 版本1(格式类似7.0.12)
 * @param v2 版本2(格式类似7.0.12)
 */
export const verCompare = (v1: string, v2: string) => {
  // 兜底处理，如果没有传v1或v2，则默认是0
  const formatV1 = v1 || '0';
  const formatV2 = v2 || '0';
  return compareVersions(formatV1, formatV2);
};

/**
 * 比较微信版本大于等于ver
 * @param ver 比较的版本
 */
export const isWexinVerLargeOrEqualThan = (ver: string) => {
  const curVer = getWeixinVer();
  return verCompare(curVer, ver) > -1;
};


/**
 * 判断安卓、ios的版本是否大于等于指定版本
 * 如果对android系统没版本要求，则传入如 android: '0.0.0'
 * @param minVer 两个系统的最低版本
 */
export const isOSVerLargeOrEqualThan = (minVer: {
  android: string;
  iphone: string;
}) => {
  const { type, version } = getOSInfoObj();
  if (type === 'none') return false;
  const ver = minVer[type];
  if (!ver) {
    // 这里处理了type为none，或者没有传入某类最低版本的场景
    return false;
  }
  return verCompare(version, ver) > -1;
};

/**
 * 判断在所有的系统下最小要求支持的微信版本
 * @param minVersions 微信最小版本号（需要指定每个系统下的版本号，如：iphone: "8.0.20"，android:"8.0.21",如果对版本没要求，可传入0.0.0）
 * @returns
 */
export const isAllWxVersionSupported = (minVersions: {iphone: string, android: string}) => {
  const os = getOSInfoObj();
  const version = (os.type === 'iphone') ? minVersions.iphone : minVersions.android;
  return isWexinVerLargeOrEqualThan(version);
};

/** 判断是否在微信浏览器内 */
export const isInWeixin = () => /MicroMessenger/i.test(getUserAgent());

/**
 * 判断是否在小程序内
 */
// @ts-ignore
export const isInMiniProgram = () => /miniprogram/i.test(getUserAgent()) || window.__wxjs_environment === 'miniprogram';

/**
 * H5 被小程序内嵌时，获取宿主小程序appId
 */
export const getMiniProgramAppId = () => {
  const ua = getUserAgent();
  const pattern = /miniProgram\/(\w+)/;
  const result = pattern.exec(ua);
  if (result?.[1]) {
    return result[1];
  }
  return '';
};

/**
 * 将hex版本号转为描述版本号，如0x28001320 => 安卓8.0.51.32。
 * iOS版本号区间 [0x10000000 , 0x1FFFFFFF]（16进制），对应十进制 [268435456 , 536870911]
   android版本号区间 0x20000000 - 0x2FFFFFFF（16进制），对应十进制 [536870912, 805306367]
   例如 8.0.19.32 这样一个客户端版本，在iOS上是 0x18001320，在android上是 0x28001320
 * @param hexVersion hex版本号：如0x28003d37
 */
export const hexWxVersionToDesc = (hexVersion: number): string => {
  const hexVersionStr = `0x${hexVersion.toString(16)}`;
  const regx = /([0-9a-zA-Z])([0-9a-zA-Z]{2})([0-9a-zA-Z]{2})([0-9a-zA-Z]{2})$/;
  const ms = hexVersionStr.match(regx);
  if (ms) {
    return ms
      .filter((item, index) => index > 0)
      .map(item => parseInt(item, 16))
      .join('.');
  }
  return '';
};
